import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { FileText, FileDown, Trash2, Sparkles, Folder } from "lucide-react";

const fmtDT = (s) => s ? new Date(s).toLocaleString("it-IT") : "—";
const fmtKB = (n) => {
  const kb = (Number(n || 0) / 1024);
  return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb/1024).toFixed(2)} MB`;
};

/** Iter122ce · SuperAdmin · Report interni (cartella persistente /app/reports/). */
export default function SuperAdminReports() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/superadmin/reports").then((r) => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const generaPiano = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/superadmin/reports/genera/piano-economico");
      toast.success(`Generato ${data.nome}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore generazione"); }
    setBusy(false);
  };

  // Iter122dt · Workflow Trial → Versione Completa (documento commerciale interno)
  const generaWorkflowTrial = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/superadmin/reports/genera/workflow-trial");
      toast.success(`Generato ${data.nome}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore generazione"); }
    setBusy(false);
  };

  // Iter122ez · Tutorial RAC per condòmini (documentazione tecnica)
  const generaTutorialRac = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/superadmin/reports/genera/tutorial-rac");
      toast.success(`Generato ${data.nome}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore generazione"); }
    setBusy(false);
  };

  const scarica = async (f) => {
    try {
      const res = await api.get(`/superadmin/reports/download/${encodeURIComponent(f.nome)}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = f.nome;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Errore download"); }
  };

  const elimina = async (f) => {
    if (!window.confirm(`Eliminare definitivamente il report ${f.nome}?`)) return;
    try {
      await api.delete(`/superadmin/reports/${encodeURIComponent(f.nome)}`);
      toast.success("Report eliminato"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  return (
    <div className="space-y-4" data-testid="superadmin-reports">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-brand" />
          <h3 className="font-display text-lg font-bold text-slate-800">Report interni</h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" className="h-9 bg-brand hover:bg-brand-dark" onClick={generaPiano} disabled={busy}
                  data-testid="report-genera-piano">
            <Sparkles className="w-4 h-4 mr-1" /> Rigenera Piano economico
          </Button>
          <Button size="sm" variant="outline" className="h-9 border-brand text-brand hover:bg-brand/5"
                  onClick={generaWorkflowTrial} disabled={busy}
                  data-testid="report-genera-workflow-trial">
            <Sparkles className="w-4 h-4 mr-1" /> Rigenera Workflow Trial
          </Button>
          <Button size="sm" variant="outline" className="h-9 border-brand text-brand hover:bg-brand/5"
                  onClick={generaTutorialRac} disabled={busy}
                  data-testid="report-genera-tutorial-rac">
            <Sparkles className="w-4 h-4 mr-1" /> Rigenera Tutorial RAC
          </Button>
        </div>
      </div>

      <Card className="p-3 bg-slate-50 border-2 border-slate-200 text-xs text-slate-600 flex items-start gap-2" data-testid="reports-info">
        <Folder className="w-4 h-4 text-brand shrink-0 mt-0.5" />
        <div>
          I PDF di sviluppo sono archiviati sul server in <code className="bg-white px-1 rounded">/app/reports/</code>.
          La cartella sopravvive ai riavvii e ai deploy. Al momento è archiviato il <b>Piano economico Ginius</b>;
          eventuali nuovi report verranno inseriti qui in automatico.
        </div>
      </Card>

      {items.length === 0 && (
        <Card className="p-4 text-center text-slate-400 text-sm" data-testid="reports-empty">
          Nessun report archiviato. Clicca &quot;Rigenera Piano economico&quot; per crearne uno.
        </Card>
      )}

      {items.map((f) => (
        <Card key={f.nome} className="p-3 sm:p-4 border-2 flex flex-wrap items-center gap-3" data-testid={`report-${f.nome}`}>
          <FileText className="w-5 h-5 text-brand shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="font-mono text-sm text-slate-800 truncate">{f.nome}</div>
            <div className="text-xs text-slate-500">{fmtKB(f.size)} · aggiornato {fmtDT(f.modificato_at)}</div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-9" onClick={() => scarica(f)}
                    data-testid={`report-download-${f.nome}`}>
              <FileDown className="w-4 h-4 mr-1" /> Scarica
            </Button>
            <Button size="sm" variant="outline" className="h-9 text-red-600 border-red-300 hover:bg-red-50"
                    onClick={() => elimina(f)} data-testid={`report-delete-${f.nome}`} title="Elimina definitivamente">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </Card>
      ))}
    </div>
  );
}
